import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetPath } from '../utils/assetHelper';
import './SettingsPrivacyPage.scss';

interface PrivacyOption {
  id: string;
  label: string;
  icon: string;
  checked: boolean;
}

const SettingsPrivacyPage: React.FC = () => {
  // State for profile visibility
  const [profileVisibility, setProfileVisibility] = useState('public');
  
  // State for privacy options
  const [privacyOptions, setPrivacyOptions] = useState<PrivacyOption[]>([
    { id: 'autoShareAchievements', label: 'Auto-Share Achievements', icon: 'bi bi-archive-fill', checked: true },
    { id: 'dataSharing', label: 'Data Sharing Preferences', icon: 'bi bi-graph-up', checked: false },
    { id: 'activityTracking', label: 'Activity Tracking', icon: 'bi bi-activity', checked: true },
    { id: 'personalizedAds', label: 'Personalized Ads', icon: 'bi bi-badge-ad-fill', checked: false }
  ]);

  // Handle profile visibility change
  const handleProfileVisibilityChange = (value: string) => {
    setProfileVisibility(value);
    
    // In a real app, this would save to backend
    console.log('Profile visibility changed to:', value);
  };

  // Handle privacy option toggle
  const handlePrivacyOptionToggle = (id: string) => {
    setPrivacyOptions(prev => 
      prev.map(option => 
        option.id === id ? { ...option, checked: !option.checked } : option
      )
    );
    
    // In a real app, this would save to backend
    console.log('Privacy option toggled:', id);
  };

  // Set document title on component mount
  useEffect(() => {
    document.title = 'Settings - Privacy | Phantom Clash';
    
    // Initialize tooltips if Bootstrap is available
    if ((window as any).bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function(tooltipTriggerEl) {
        return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  return (
    <>
      {/* Page Menu */}
      <div className="c-page-menu">
        <ul>
          <li><Link to="/settings-profile">Profile</Link></li>
          <li><Link to="/settings-notifications">Notifications</Link></li>
          <li><Link to="/settings-wallet">Wallet</Link></li>
          <li><Link to="/settings-security">Security</Link></li>
          <li className="active"><Link to="/settings-privacy">Privacy</Link></li>
          <li><Link to="/settings-display">Theme & Display</Link></li>
          <li><Link to="/settings-advanced">Advanced Settings</Link></li>
        </ul>
      </div>

      <div className="d-block w-100">
        <div className="box position-relative z-1 overflow-hidden mt-2">
          <div className="c-content-box c-content-box--xspadding c-content-box--setting-width">
            <div className="c-content-box__title">
              <h1 style={{ backgroundImage: 'none' }}><i className="bi bi-eye-fill"></i> Privacy Settings</h1>
              <div className="c-content-box__title-info"></div>
            </div>
            <div className="c-content-box__content">
              <h5 className="c-content-mini-title gray">Control the visibility of your profile and data sharing options</h5>
              
              {/* Profile Visibility */}
              <div className="c-content-box__checkboxlist">
                <div className="c-content-box__checkboxlist-item flex-column align-items-baseline pt-0">
                  <h3 className="mb-md-4 mb-3">Profile Visibility</h3>
                  <div className="d-md-flex d-block align-items-center">
                    <div className="me-4 mb-md-0 mb-3">
                      <input 
                        className="c-radio" 
                        id="public" 
                        name="profileVisibility" 
                        type="radio"
                        checked={profileVisibility === 'public'}
                        onChange={() => handleProfileVisibilityChange('public')}
                      />
                      <label className="c-radio__label" htmlFor="public">
                        Public <small><i className="bi bi-globe"></i></small>
                      </label>
                    </div>
                    <div className="me-4 mb-md-0 mb-3">
                      <input 
                        className="c-radio" 
                        id="friendsOnly" 
                        name="profileVisibility" 
                        type="radio"
                        checked={profileVisibility === 'friendsOnly'}
                        onChange={() => handleProfileVisibilityChange('friendsOnly')}
                      />
                      <label className="c-radio__label" htmlFor="friendsOnly">
                        Friends Only <small><i className="bi bi-people-fill"></i></small>
                      </label>
                    </div>
                    <div>
                      <input 
                        className="c-radio" 
                        id="private" 
                        name="profileVisibility" 
                        type="radio"
                        checked={profileVisibility === 'private'}
                        onChange={() => handleProfileVisibilityChange('private')}
                      />
                      <label className="c-radio__label" htmlFor="private">
                        Private <small><i className="bi bi-lock-fill"></i></small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Privacy Options */}
              <div className="c-content-box__checkboxlist">
                {/* Auto-Share Achievements */}
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className={privacyOptions[0].icon}></i> {privacyOptions[0].label}</span>
                  <label className="c-switch" id={privacyOptions[0].id}>
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={privacyOptions[0].checked}
                      onChange={() => handlePrivacyOptionToggle(privacyOptions[0].id)}
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
                
                {/* Data Sharing Preferences */}
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className={privacyOptions[1].icon}></i> {privacyOptions[1].label}</span>
                  <label className="c-switch" id={privacyOptions[1].id}>
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={privacyOptions[1].checked}
                      onChange={() => handlePrivacyOptionToggle(privacyOptions[1].id)}
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
                
                {/* Activity Tracking */}
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className={privacyOptions[2].icon}></i> {privacyOptions[2].label}</span>
                  <label className="c-switch" id={privacyOptions[2].id}>
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={privacyOptions[2].checked}
                      onChange={() => handlePrivacyOptionToggle(privacyOptions[2].id)}
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
                
                {/* Personalized Ads */}
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span><i className={privacyOptions[3].icon}></i> {privacyOptions[3].label}</span>
                  <label className="c-switch" id={privacyOptions[3].id}>
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={privacyOptions[3].checked}
                      onChange={() => handlePrivacyOptionToggle(privacyOptions[3].id)}
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>
              </div>
              
              {/* Data Management */}
              <h3 className="c-content-mini-title-2 mb-4 mt-md-4 mt-3">Data Management</h3>
              <div className="d-flex flex-md-row flex-column align-items-center">
                <a 
                  href="" 
                  className="c-button c-button--bordered c-button--xl c-button--grayicon mt-4 me-2"
                >
                  <i className="bi bi-download"></i> Download My Data
                </a>
                <a 
                  href="" 
                  className="c-button c-button--bordered c-button--xl c-button--grayicon mt-4 c-button--danger"
                >
                  <i className="bi bi-trash"></i> Delete Account
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPrivacyPage; 